import { useMemo, useEffect, useRef, useState } from 'react'
import { urlForImage } from 'lib/sanity.image'
import { useTransform, useMotionValue, motion } from 'motion/react'
import { Button } from 'components/Global/Button'

import { ReactLenis, useLenis } from 'lenis/react'
import Image from 'next/image'
import { PortableText } from '@portabletext/react'

export const WorldCarousel = ({ world }) => {
  const lenisRef = useRef<any>(null)
  const filteredItems = useMemo(() => {
    return world.items.filter(
      (item) =>
        item.hidden !== true && item._type === 'socialVideo' && item.mediaType
    )
  }, [world.items])

  useEffect(() => {
    const raf = (time) => {
      if (lenisRef.current) {
        lenisRef.current?.raf(time)
      }
      requestAnimationFrame(raf)
    }

    requestAnimationFrame(raf)
  }, [])

  return (
    <ReactLenis
      root={false}
      ref={lenisRef}
      autoRaf={false}
      options={{
        orientation: 'horizontal',
        gestureOrientation: 'horizontal',
        infinite: false,
        smoothWheel: true,
        syncTouch: true,
      }}
      className="scrollbar-hidden w-full touch-pan-x overflow-scroll scroll-auto"
    >
      <div className="relative w-full whitespace-nowrap pb-[200px] pt-[120px] md:pb-[180px]">
        {filteredItems.map((item) => {
          return <Item key={item._key} item={item} />
        })}
      </div>
    </ReactLenis>
  )
}

const Item = ({ item }) => {
  const [muted, setMuted] = useState(true)
  const itemRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const x = useMotionValue(0)
  const o = useMotionValue(0)
  const scale = useTransform(x, [-400, -50, 0, 50, 400], [1, 1.2, 1.2, 1.2, 1])
  const opacity = useTransform(o, [-400, -50, 0, 50, 400], [0, 1, 1, 1, 0])

  useLenis(({ scroll }) => {
    if (itemRef.current) {
      x.set(
        itemRef.current.offsetLeft -
          scroll -
          window.innerWidth / 2 +
          itemRef.current.clientWidth / 2
      )
      o.set(
        itemRef.current.offsetLeft -
          scroll -
          window.innerWidth / 2 +
          itemRef.current.clientWidth / 2
      )

      if (scale.get() > 1) {
        videoRef.current?.play()
      } else {
        videoRef.current?.pause()
      }
    }
  })

  return (
    <motion.div
      ref={itemRef}
      style={{ scale: scale }}
      className="group relative mr-[60px] inline-block w-[50vw] first-of-type:ml-[25vw] last-of-type:mr-[25vw] md:mr-[150px] md:w-[320px] md:first-of-type:ml-[40vw] md:last-of-type:mr-[40vw] lg:w-[400px]"
    >
      <div className="relative aspect-[2/3.1] overflow-hidden rounded-6">
        {item.sound && item.mediaType === 'video' ? (
          <Button
            as="button"
            className="background-blur ease absolute bottom-12 left-1/2 z-[2] w-auto  -translate-x-1/2 py-6 text-center text-11 leading-[100%] opacity-0 !transition-all duration-300 group-hover:opacity-100"
            onClick={() => setMuted(!muted)}
          >
            {muted ? 'Unmute' : 'Mute'}
          </Button>
        ) : null}
        {item.mediaType === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={videoRef}
            src={item.video.url}
            muted={muted}
            playsInline
            preload="none"
            loop
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : item.mediaType === 'image' ? (
          <Image
            src={urlForImage(item.image).width(900).url()}
            fill
            style={{ objectFit: 'cover' }}
            placeholder="blur"
            blurDataURL={item.image.lqip}
            alt=""
          />
        ) : null}
      </div>
      {item.caption ? (
        <motion.div
          className="absolute top-[calc(100%+28px)] w-full whitespace-normal rounded-8 bg-white px-8 py-14 text-12 leading-120 text-midGray shadow-md"
          style={{ opacity: opacity }}
        >
          <div className="w-full max-w-[300px]">
            <PortableText value={item.caption} />
          </div>
          <div className="pt-10 text-gray">
            <a
              href={`https://tiktok.com/@${item.handle}`}
              rel="noreferrer noopener"
              target="_blank"
              className="underline"
            >
              @{item.handle}
            </a>
            {item.platform ? <span>, {item.platform}</span> : null}
          </div>
        </motion.div>
      ) : null}
    </motion.div>
  )
}
